/**
 * /bookmarks — the authenticated user's personal reading list.
 *
 * Groups saved links by their `tag` (which is the topic query used
 * when the bookmark was created — e.g. "Portland Fire Department").
 * Every row supports one-click removal and open-in-new-tab.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Bookmark, ExternalLink, Loader2, Trash2, Search, Tag } from "lucide-react";

function faviconFor(url) {
  try {
    return `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=32`;
  } catch {
    return null;
  }
}

export default function BookmarksDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setLoading(true);
    http.get("/bookmarks")
      .then((r) => alive && setItems(r.data?.items || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [user]);

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const filtered = q
      ? items.filter((b) => (b.title || "").toLowerCase().includes(q) || (b.snippet || "").toLowerCase().includes(q) || (b.tag || "").toLowerCase().includes(q))
      : items;
    const map = new Map();
    for (const b of filtered) {
      const t = (b.tag || "Untagged").trim() || "Untagged";
      if (!map.has(t)) map.set(t, []);
      map.get(t).push(b);
    }
    // Sort groups: largest first, "Untagged" last.
    return Array.from(map.entries())
      .sort(([a, aa], [b, bb]) => (a === "Untagged" ? 1 : b === "Untagged" ? -1 : bb.length - aa.length));
  }, [items, filter]);

  const remove = async (url) => {
    try {
      await http.delete(`/bookmarks?url=${encodeURIComponent(url)}`);
      setItems((prev) => prev.filter((b) => b.url !== url));
      toast.success("Bookmark removed");
    } catch {
      toast.error("Couldn't remove");
    }
  };

  if (authLoading) return <div className="max-w-4xl mx-auto py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" /></div>;
  if (!user) return <Navigate to="/login?next=/bookmarks" replace />;

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-6 py-10" data-testid="bookmarks-dashboard">
      <div className="flex items-center gap-2 mb-2">
        <Bookmark className="w-6 h-6" style={{ color: "#B45309" }} />
        <h1 className="font-display text-3xl">My Bookmarks</h1>
      </div>
      <p className="text-sm opacity-70 mb-6">
        Everything you've saved from Related Links panels across Social Pulse.
        Grouped by topic.
      </p>

      {/* Search */}
      <div className="mb-6 relative">
        <Search className="w-4 h-4 absolute left-3 top-3 opacity-50" />
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search your bookmarks…"
          className="w-full rounded-lg border pl-9 pr-3 py-2 text-sm bg-transparent"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="bookmarks-search"
        />
      </div>

      {loading ? (
        <div className="text-sm opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="wofy-card text-center py-12">
          <p className="text-sm opacity-70">
            You haven't saved anything yet. Look for the{" "}
            <Bookmark className="w-4 h-4 inline mx-0.5" /> icon next to any Related Link
            across the site.
          </p>
        </div>
      ) : (
        <div className="space-y-8" data-testid="bookmarks-groups">
          {groups.map(([tag, entries]) => (
            <section key={tag} data-testid={`bookmarks-group-${tag}`}>
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-3.5 h-3.5" style={{ color: "#B45309" }} />
                <h2 className="font-display text-lg">{tag}</h2>
                <span className="text-xs font-mono opacity-60">{entries.length}</span>
              </div>
              <ul className="grid sm:grid-cols-2 gap-2">
                {entries.map((b) => {
                  const fav = faviconFor(b.url);
                  return (
                    <li key={b.url} className="rounded-xl border bg-white p-3 flex gap-3 hover:shadow-sm transition" style={{ borderColor: "var(--wofy-border)" }} data-testid={`bookmark-${encodeURIComponent(b.url)}`}>
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
                        {fav ? <img src={fav} alt="" className="w-5 h-5" loading="lazy" /> : <Bookmark className="w-4 h-4 opacity-60" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <a
                          href={b.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium hover:underline break-words"
                          style={{ color: "#0F172A" }}
                        >
                          {b.title}
                          <ExternalLink className="inline w-3 h-3 ml-1 opacity-50" />
                        </a>
                        {b.snippet && <div className="text-xs opacity-70 mt-1 line-clamp-2">{b.snippet}</div>}
                      </div>
                      <button
                        onClick={() => remove(b.url)}
                        className="shrink-0 opacity-50 hover:opacity-100 hover:text-red-600 transition"
                        title="Remove bookmark"
                        aria-label="Remove bookmark"
                        data-testid={`bookmark-remove-${encodeURIComponent(b.url)}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
